import { Command as CommandPrimitive } from 'cmdk'
import { useEffect, type ComponentPropsWithoutRef, type ReactNode } from 'react'

import { cn } from '@/shared/lib/cn'
import { useT } from '@/shared/i18n'
import { Portal } from './Portal'

/**
 * El primitivo del conmutador, sobre `cmdk` (el mismo que usa shadcn/Command).
 *
 * Lo que aporta `cmdk` y no queremos reescribir: el filtrado por puntaje, el
 * `aria` de combobox/listbox completo, y la navegación con flechas que salta
 * los encabezados de grupo. Lo que aportamos nosotros es el vestido: hairline +
 * superficie, cero sombras, tipografía de "Consola".
 *
 * El diálogo es propio y no `Command.Dialog` a propósito: `Command.Dialog` trae
 * el Dialog de Radix, que en jsdom necesita media docena de polyfills y, en
 * esta app, sería la única pieza de Radix de todo el árbol.
 */

export interface CommandDialogProps {
  open: boolean
  onClose: () => void
  label: string
  children: ReactNode
}

export function CommandDialog({ open, onClose, label, children }: CommandDialogProps) {
  const t = useT()

  useEffect(() => {
    if (!open) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [open, onClose])

  if (!open) return null

  return (
    <Portal>
      <div className="fixed inset-0 z-50 flex items-start justify-center p-4 pt-[12vh]">
        <button
          type="button"
          aria-label={t('common.ui.closeDialog')}
          onClick={onClose}
          className="absolute inset-0 bg-bg/70"
        />

        <div
          role="dialog"
          aria-modal="true"
          aria-label={label}
          className="hairline relative w-full max-w-[560px] overflow-hidden rounded-[var(--radius-card)] bg-surface-1"
        >
          <CommandPrimitive label={label} className="flex flex-col">
            {children}
          </CommandPrimitive>
        </div>
      </div>
    </Portal>
  )
}

export function CommandInput(props: ComponentPropsWithoutRef<typeof CommandPrimitive.Input>) {
  return (
    <div className="hairline-b flex items-center px-3">
      <CommandPrimitive.Input
        {...props}
        className={cn(
          'h-10 w-full bg-transparent text-sm text-text-1 outline-none placeholder:text-text-3',
          props.className,
        )}
      />
    </div>
  )
}

export function CommandList(props: ComponentPropsWithoutRef<typeof CommandPrimitive.List>) {
  return (
    <CommandPrimitive.List
      {...props}
      className={cn('max-h-[52vh] overflow-y-auto overscroll-contain p-1.5', props.className)}
    />
  )
}

export function CommandEmpty(props: ComponentPropsWithoutRef<typeof CommandPrimitive.Empty>) {
  return (
    <CommandPrimitive.Empty
      {...props}
      className={cn('px-3 py-6 text-center text-xs text-text-3', props.className)}
    />
  )
}

export function CommandGroup(props: ComponentPropsWithoutRef<typeof CommandPrimitive.Group>) {
  return (
    <CommandPrimitive.Group
      {...props}
      className={cn(
        'flex flex-col',
        '[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5',
        '[&_[cmdk-group-heading]]:text-2xs [&_[cmdk-group-heading]]:tracking-wide',
        '[&_[cmdk-group-heading]]:text-text-3 [&_[cmdk-group-heading]]:uppercase',
        props.className,
      )}
    />
  )
}

export function CommandItem(props: ComponentPropsWithoutRef<typeof CommandPrimitive.Item>) {
  return (
    <CommandPrimitive.Item
      {...props}
      className={cn(
        'flex cursor-pointer items-center gap-2 rounded-[var(--radius-control)] px-2 py-2 text-sm text-text-2',
        'data-[selected=true]:bg-surface-2 data-[selected=true]:text-text-1',
        props.className,
      )}
    />
  )
}

export { CommandPrimitive }
